import { canonicalizeField } from '@/shared/opensearch/utils/prefixer'

import type { SearchFilter } from './types'

/** `include` is the +, `exclude` the − action on a field value. */
export type FilterDirection = 'include' | 'exclude'

const OPERATORS = {
	include: { single: 'eq', multi: 'in' },
	exclude: { single: 'neq', multi: 'not_in' },
} as const

/**
 * Add one value to the `ft` array for a +/− action.
 *
 * Each field holds at most one filter per direction: the first value is an
 * `eq`/`neq`, a second one upgrades it to `in`/`not_in`. A value that is
 * already there returns `filters` unchanged (same reference), so a repeat
 * click writes nothing. Separate `eq` filters for one field would be joined
 * by AND and match nothing.
 *
 * @param field - Display or canonical field name; stored canonical.
 */
export function withValue(
	filters: Array<SearchFilter>,
	direction: FilterDirection,
	field: string,
	value: string,
): Array<SearchFilter> {
	const { single, multi } = OPERATORS[direction]
	const canonical = canonicalizeField(field)
	const idx = filters.findIndex(
		(f) => f.f === canonical && (f.o === single || f.o === multi),
	)

	if (idx === -1) return [...filters, { f: canonical, o: single, v: value }]

	const existing = filters[idx]
	const values = Array.isArray(existing.v) ? existing.v : [existing.v]
	if (values.includes(value)) return filters

	const next = [...filters]
	next[idx] = { f: canonical, o: multi, v: [...values, value] }
	return next
}
